import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, Settings as SettingsIcon, PlayCircle, BarChart3, Webhook, Link as LinkIcon, Megaphone, Newspaper, Trophy, Crown, DollarSign, Gift, CalendarClock, Scissors, UserCog } from 'lucide-react';
import { adminMe, setAuthToken } from '../lib/api';
import AdminSidebar from '../components/admin/AdminSidebar';
import AdminSettingsTab from '../components/admin/AdminSettingsTab';
import AdminVideosTab from '../components/admin/AdminVideosTab';
import AdminAnalyticsTab from '../components/admin/AdminAnalyticsTab';
import AdminBrokerAnalytics from '../components/admin/AdminBrokerAnalytics';
import AdminPostbackSetup from '../components/admin/AdminPostbackSetup';
import AdminShortlinksTab from '../components/admin/AdminShortlinksTab';
import AdminAdsTab from '../components/admin/AdminAdsTab';
import AdminBlogTab from '../components/admin/AdminBlogTab';
import AdminStudentWinsTab from '../components/admin/AdminStudentWinsTab';
import AdminEliteNetworkTab from '../components/admin/AdminEliteNetworkTab';
import AdminCpaTab from '../components/admin/AdminCpaTab';
import AdminGiveawaysTab from '../components/admin/AdminGiveawaysTab';
import AdminBookingsTab from '../components/admin/AdminBookingsTab';
import AdminClippersTab from '../components/admin/AdminClippersTab';
import AdminStaffTab from '../components/admin/AdminStaffTab';
import AdminQuotexPostbackSetup from '../components/admin/AdminQuotexPostbackSetup';
import { Button } from '../components/ui/button';

const MOBILE_TABS = [
  { key: 'settings', label: 'Settings', icon: SettingsIcon },
  { key: 'videos', label: 'Videos', icon: PlayCircle },
  { key: 'wins', label: 'Wins', icon: Trophy },
  { key: 'elite', label: 'Elite', icon: Crown },
  { key: 'analytics', label: 'Analytics', icon: BarChart3 },
  { key: 'broker', label: 'Broker', icon: Webhook },
  { key: 'cpa', label: 'CPA', icon: DollarSign },
  { key: 'giveaways', label: 'Giveaways', icon: Gift },
  { key: 'bookings', label: 'Bookings', icon: CalendarClock },
  { key: 'clippers', label: 'Clippers', icon: Scissors },
  { key: 'staff', label: 'Staff', icon: UserCog },
  { key: 'ads', label: 'Ads', icon: Megaphone },
  { key: 'blog', label: 'Blog', icon: Newspaper },
  { key: 'shortlinks', label: 'Links', icon: LinkIcon },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [tab, setTabState] = useState(() => {
    // Read initial tab from URL query (?tab=analytics|videos|settings|broker)
    try {
      const sp = new URLSearchParams(window.location.search);
      const t = (sp.get('tab') || '').toLowerCase();
      if (['settings', 'videos', 'wins', 'elite', 'analytics', 'broker', 'cpa', 'giveaways', 'bookings', 'clippers', 'ads', 'blog', 'shortlinks'].includes(t)) return t;
    } catch { /* noop */ }
    return 'analytics';
  });
  const [email, setEmail] = useState('');

  // Keep URL in sync with tab for deep-linking + shareable URLs
  function setTab(next) {
    setTabState(next);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', next);
      window.history.replaceState({}, '', url.toString());
    } catch { /* noop */ }
  }

  useEffect(() => {
    document.title = 'AlveeTrade Admin — Dashboard';
    (async () => {
      try {
        const me = await adminMe();
        setEmail(me.email);
        setReady(true);
      } catch {
        setAuthToken(null);
        nav('/admin/nayemur/alvee/login');
      }
    })();
  }, [nav]);

  function logout() {
    setAuthToken(null);
    toast.success('Logged out');
    nav('/admin/nayemur/alvee/login');
  }

  if (!ready) {
    return (
      <div className="min-h-screen bg-[hsl(0_0%_4%)] text-white flex items-center justify-center">
        <div className="text-sm text-muted-foreground">Loading admin…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(0_0%_4%)] text-white flex">
      <AdminSidebar tab={tab} setTab={setTab} email={email} onLogout={logout} />
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar — always visible on all screen sizes */}
        <div className="border-b border-border px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 bg-[hsl(240_6%_7%)]" data-testid="admin-topbar">
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-semibold capitalize truncate">{tab}</h1>
            <p className="text-[10px] sm:text-xs text-muted-foreground truncate">AlveeTrade control center</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <div className="text-[11px] sm:text-xs text-muted-foreground hidden sm:block truncate max-w-[200px]" data-testid="admin-email-label">{email}</div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={logout}
              className="h-9 gap-2 border-white/10 bg-white/[0.02] text-white hover:bg-white/[0.06] hover:text-white"
              data-testid="admin-topbar-logout"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>

        {/* Mobile tab switcher — only visible when sidebar is hidden */}
        <div className="md:hidden border-b border-border bg-[hsl(240_6%_7%)] px-3 py-2 flex gap-1 overflow-x-auto" data-testid="admin-mobile-tabs">
          {MOBILE_TABS.map(t => {
            const active = tab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-mobile-tab-${t.key}`}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${active ? 'bg-[rgba(245,185,66,0.12)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.3)]' : 'text-muted-foreground hover:text-white'}`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-4 sm:p-6 max-w-6xl">
          {tab === 'settings' && <AdminSettingsTab />}
          {tab === 'videos' && <AdminVideosTab />}
          {tab === 'analytics' && <AdminAnalyticsTab />}
          {tab === 'broker' && (
            <div className="space-y-5">
              <AdminBrokerAnalytics />
              <AdminPostbackSetup />
              <AdminQuotexPostbackSetup />
            </div>
          )}
          {tab === 'shortlinks' && <AdminShortlinksTab />}
          {tab === 'ads' && <AdminAdsTab />}
          {tab === 'blog' && <AdminBlogTab />}
          {tab === 'wins' && <AdminStudentWinsTab />}
          {tab === 'elite' && <AdminEliteNetworkTab />}
          {tab === 'cpa' && <AdminCpaTab />}
          {tab === 'giveaways' && <AdminGiveawaysTab />}
          {tab === 'bookings' && <AdminBookingsTab />}
          {tab === 'clippers' && <AdminClippersTab />}
          {tab === 'staff' && <AdminStaffTab />}
        </div>
      </div>
    </div>
  );
}
